/*
  Kitenzo bundle builder bridge (CU-869ee6a6m). Ported from the POC theme
  feature/kitenzo-custom-bundle-page-CU-869e9ru5u, which never reached git,
  and extended for the restyle. Loaded only on /apps/bundles/* pages by
  snippets/kitenzo-bundle-page.liquid.

  The builder is a React app the theme cannot edit, so everything here is
  additive: new nodes appended or prepended beside the app's own, data
  attributes and custom properties on the <bundle-builder-bundle-v1> host.
  Nothing moves a node React owns; the one thing rewritten on the app's own
  nodes is the card images' srcset and sizes (4).

  1. bbModifyAddToCartPayload / bbAfterAddToCart — the hooks Kitenzo calls
     around its add-to-cart. In native bundle mode Kitenzo builds one bundle
     line itself and the stamped properties never reach the cart; they stay
     for the app's non-native fallback, where every item is its own line and
     the visible "Included In" property names the bundle. The after-hook
     hands off to the theme's cart drawer (refreshAndOpen).
  2. The tier badge on the sticky bar — "You've saved 10%. Add 2 more to
     save 12.5%" — from the app's tier pills and the selected thumbnails.
  3. The USP marquee — clones the Icon + Text items once so the CSS track
     loops seamlessly, and measures the loop period for the keyframes.
  4. Card images — requested as the PLP card requests them, CDN crops at
     the card's size, not the uncropped originals the app asks for.

  Copy such as button labels and the search placeholder is not touched here:
  those strings belong to Kitenzo's Translations settings.
*/
(() => {
  const ROOT_SELECTOR = "bundle-builder-bundle-v1";

  // A rendered product card from either renderer the app has: the Core (v2)
  // engine behind the socks, pouch and migrated two-step bundles, and the
  // legacy "Insight" template behind the original two-step bundles.
  const GRID_CARD_SELECTOR =
    ".blocks-module__productCard, .TemplateGridCard-module__template-grid-card";
  const MARQUEE_SPEED_PX_PER_SECOND = 35;

  // A card's product image in either renderer, and the request the PLP card
  // makes for the same image (snippets/product-card-media.liquid): centre
  // crops at the card's 321:400, 160–642px wide, and the card's sizes.
  const CARD_IMAGE_SELECTOR =
    ".blocks-module__productImage, .ProductImage-module__kitenzo-product-image-container img";
  const CARD_IMAGE_WIDTHS = [160, 240, 321, 460, 642];
  const CARD_IMAGE_RATIO = 400 / 321;
  const CARD_IMAGE_SIZES =
    "(min-width: 1920px) 460px, (min-width: 768px) 25vw, 50vw";

  /* ---------------------------------------------------------------- hooks */

  const createBundleId = () =>
    `kitenzo_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const readBundleName = () => {
    const fromAttr = document
      .querySelector("[data-roka-bb]")
      ?.getAttribute("data-bundle-title");
    if (fromAttr && fromAttr.trim()) return fromAttr.trim();

    return (
      document
        .querySelector(".roka-bb__title, [data-bb-title], h1")
        ?.textContent?.trim() || "Bundle"
    );
  };

  window.bbModifyAddToCartPayload = (payload) => {
    if (
      !payload ||
      !Array.isArray(payload.items) ||
      payload.items.length === 0
    ) {
      return;
    }

    const bundleId = createBundleId();
    const bundleName = readBundleName();

    payload.items.forEach((item, index) => {
      const properties =
        item.properties && typeof item.properties === "object"
          ? { ...item.properties }
          : {};

      properties._kitenzo_bundle = "1";
      properties._bundle = bundleId;
      properties._bundle_name = bundleName;
      properties["Included In"] = bundleName;
      if (index === 0) properties._bundle_main = "1";

      item.properties = properties;
    });
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Kitenzo leaves its own add-to-cart button disabled with its spinner up for
  // the rest of the page's life once a bbAfterAddToCart hook exists: it clears
  // that state only on the branch where it navigates away to /cart. Staying on
  // the page therefore strands it, and a shopper who builds a second bundle
  // meets a dead, spinning button. Measured rather than assumed:
  //
  //   - it reproduces with the hook replaced by an empty function, so it is
  //     not the theme's doing;
  //   - the state is out of reach — `window.store` is a frozen valtio snapshot
  //     from the app's first render, so it reads stale, and the app's own
  //     `clearBundle()` action does not restore the button;
  //   - emptying the builder only hides the bar, so the button comes back
  //     spinning as soon as anything is selected again.
  //
  // So the builder is re-initialised in place instead: taking the app's custom
  // element out of the document and putting it straight back runs its
  // disconnected/connected lifecycle, and it mounts again with fresh state —
  // clean selection, live button — while the cart, the drawer and the rest of
  // the page carry on untouched. No navigation and no reload, which is what
  // PO review CU-869evvk61 item 4 asked for. An earlier version of this
  // reloaded the page; that is the flicker the review reported.
  const REBUILDING_ATTRIBUTE = "data-kitenzo-rebuilding";

  const remountBuilder = async () => {
    const host = document.querySelector(ROOT_SELECTOR);
    const parent = host?.parentElement;
    if (!host || !parent) return;

    // Hide the swap: the app tears its markup down and rebuilds it, which is
    // a visible flash on the part of the page the drawer does not cover.
    document.body.setAttribute(REBUILDING_ATTRIBUTE, "");

    const nextSibling = host.nextSibling;
    host.remove();
    await sleep(50);
    parent.insertBefore(host, nextSibling);

    // Reveal once the app has rendered its grid again, or give up waiting.
    for (let i = 0; i < 60; i++) {
      if (host.querySelector(GRID_CARD_SELECTOR)) break;
      await sleep(100);
    }

    document.body.removeAttribute(REBUILDING_ATTRIBUTE);
  };

  window.bbAfterAddToCart = async () => {
    const drawer = document.querySelector("cart-drawer");

    if (drawer && typeof drawer.refreshAndOpen === "function") {
      try {
        await drawer.refreshAndOpen();
        await remountBuilder();
        return;
      } catch {
        // Fall through to the cart page.
      }
    }

    window.location.href = "/cart";
  };

  /* ----------------------------------------------------------- tier badge */

  const i18n = (() => {
    try {
      return JSON.parse(
        document.getElementById("kitenzo-bundle-i18n")?.textContent || "{}",
      );
    } catch {
      return {};
    }
  })();

  const fill = (template, vars) =>
    String(template || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key) =>
      key in vars ? String(vars[key]) : "",
    );

  // 12.5 stays "12.5", 10 stays "10" — never "10.0".
  const formatPercent = (value) => String(Number(value));

  const byCount = (a, b) => a.count - b.count;

  // A tier that saves nothing is not a tier. The bag + umbrella bundles carry
  // a flat discount and a single 0% entry in tieredDiscounts, which otherwise
  // produced "Add 2 more to save 0%" on the bar. With no tier worth naming the
  // badge stays empty, and `.kitenzo-tier:empty` takes it off the page — the
  // approved design has no message for a flat-discount bundle.
  const hasSaving = (tier) => Number.isFinite(tier.percent) && tier.percent > 0;

  // The Badge block renders one pill per tier, e.g. "3+ Save 10%".
  const readTiersFromPills = (root) => {
    const tiers = [];

    for (const pill of root.querySelectorAll(".v2-block-badge span")) {
      const match = pill.textContent
        .replace(/\s+/g, " ")
        .match(/(\d+)\s*\+.*?(\d+(?:[.,]\d+)?)\s*%/);
      if (match) {
        tiers.push({
          count: Number(match[1]),
          percent: Number(match[2].replace(",", ".")),
        });
      }
    }

    return tiers.filter(hasSaving).sort(byCount);
  };

  // Fallback when the pills carry custom copy: the bundle's own definition.
  let tiersRequest = null;
  const readTiersFromBundle = () => {
    if (tiersRequest) return tiersRequest;

    const id = window.location.pathname.split("/").filter(Boolean).pop();
    if (!/^\d+$/.test(id || "")) {
      tiersRequest = Promise.resolve([]);
      return tiersRequest;
    }

    tiersRequest = fetch(`/apps/bundles/api/v1/bundle?id=${id}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((bundle) =>
        (bundle?.tieredDiscounts || [])
          .filter(
            (tier) =>
              tier.type === "total_products" && tier.operation === "gte",
          )
          .map((tier) => ({
            count: Number(tier.value),
            percent: Number(tier.discount),
          }))
          .filter(hasSaving)
          .sort(byCount),
      )
      .catch(() => []);

    return tiersRequest;
  };

  // One thumb per product; repeats carry a quantity marker.
  const readSelectedCount = (footer) => {
    let count = 0;

    for (const thumb of footer.querySelectorAll(".v2-footer-image-wrapper")) {
      const quantity = parseInt(
        thumb.querySelector(".v2-footer-image-quantity")?.textContent || "1",
        10,
      );
      count += Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    return count;
  };

  const tierMessage = (tiers, count) => {
    if (!tiers.length || count === 0) return "";

    const current = [...tiers].reverse().find((tier) => count >= tier.count);
    const next = tiers.find((tier) => count < tier.count);

    if (current && next) {
      return fill(i18n.saved_add_more, {
        percent: formatPercent(current.percent),
        count: next.count - count,
        next_percent: formatPercent(next.percent),
      });
    }
    if (current) {
      return fill(i18n.saved, { percent: formatPercent(current.percent) });
    }
    if (next) {
      return fill(i18n.add_more, {
        count: next.count - count,
        percent: formatPercent(next.percent),
      });
    }

    return "";
  };

  const ensureTierBadge = (footer) => {
    let badge = footer.querySelector(":scope > .kitenzo-tier");

    if (!badge) {
      badge = document.createElement("p");
      badge.className = "kitenzo-tier";
      badge.setAttribute("aria-live", "polite");
      footer.prepend(badge);
    }

    return badge;
  };

  const updateTierBadge = async (root, footer) => {
    let tiers = readTiersFromPills(root);
    if (!tiers.length) tiers = await readTiersFromBundle();
    if (!footer.isConnected) return;

    const badge = ensureTierBadge(footer);
    const text = tierMessage(tiers, readSelectedCount(footer));
    if (badge.textContent !== text) badge.textContent = text;
  };

  /* -------------------------------------------------------------- marquee */

  // One clone per item, kept in step with its original: the app renders the
  // item icons a beat after the items themselves, so a copy taken at mount
  // would stay iconless. Runs on every sync, so a later re-render of the block
  // (new copy, a fifth item) reaches the clones too.
  const syncClones = (track) => {
    const originals = [...track.children].filter(
      (item) => !item.hasAttribute("data-kitenzo-clone"),
    );
    const clones = [...track.querySelectorAll(":scope > [data-kitenzo-clone]")];
    if (!originals.length) return false;

    if (clones.length !== originals.length) {
      for (const clone of clones) clone.remove();
      for (const item of originals) {
        const clone = item.cloneNode(true);
        clone.setAttribute("data-kitenzo-clone", "");
        clone.setAttribute("aria-hidden", "true");
        track.append(clone);
      }
      return true;
    }

    originals.forEach((item, index) => {
      if (clones[index].innerHTML !== item.innerHTML) {
        clones[index].innerHTML = item.innerHTML;
      }
    });
    return true;
  };

  const setupMarquee = (root) => {
    const track = root.querySelector(".v2-icon-text-grid");
    if (!track || !syncClones(track)) return;

    const measure = () => {
      const first = track.firstElementChild;
      const firstClone = track.querySelector("[data-kitenzo-clone]");
      if (!first || !firstClone) return;

      // Both children ride the same transform, so the distance between them
      // is the loop period however far the track has travelled.
      const period =
        firstClone.getBoundingClientRect().left -
        first.getBoundingClientRect().left;
      if (!(period > 0)) return;

      root.style.setProperty("--kitenzo-usp-period", `${period}px`);
      root.style.setProperty(
        "--kitenzo-usp-duration",
        `${Math.round(period / MARQUEE_SPEED_PX_PER_SECOND)}s`,
      );
      root.setAttribute("data-kitenzo-usp", "ready");
    };

    measure();

    if (!track.dataset.kitenzoObserved && "ResizeObserver" in window) {
      track.dataset.kitenzoObserved = "true";
      new ResizeObserver(measure).observe(track);
    }
  };

  /* ---------------------------------------------------------- card images */

  // The app draws each card from the uncropped original and leaves the
  // resizing to the browser. The legacy template's sizes overstates the slot
  // — calc(100vw / 4), earlier 100vw — so a 2x screen downloads the 1070px
  // copy of a square image, or the 2048px original, and shrinks it into the
  // ~290 × 362 box itself. Shrunk that far, the canvas weave aliases into
  // contour-like bands wherever the browser does not filter a downscale
  // (CU-869f6212n; Chrome on Windows draws it clean, so it reads as one
  // machine's problem). The PLP card never shows it: it asks the CDN for a
  // crop at the card's own size, so the CDN does the resizing. The cards here
  // are pointed at those same crops. Only srcset and sizes change.
  const cardImageSrcset = (source) => {
    let url;
    try {
      url = new URL(source, window.location.href);
    } catch {
      return "";
    }

    // Only Shopify's image CDN understands the crop parameters.
    if (
      url.hostname !== "cdn.shopify.com" &&
      !url.pathname.startsWith("/cdn/shop/")
    ) {
      return "";
    }

    for (const key of ["crop", "height", "width"]) {
      url.searchParams.delete(key);
    }

    return CARD_IMAGE_WIDTHS.map((width) => {
      const candidate = new URL(url);
      candidate.searchParams.set("crop", "center");
      candidate.searchParams.set(
        "height",
        String(Math.round(width * CARD_IMAGE_RATIO)),
      );
      candidate.searchParams.set("width", String(width));
      // Sorted, the query is the PLP card's byte for byte.
      candidate.searchParams.sort();
      return `${candidate.href} ${width}w`;
    }).join(", ");
  };

  // Idempotent: a srcset written here maps back to itself, so only an image
  // the app has just rendered, or has given a new source, is touched.
  const cropCardImages = (root) => {
    for (const image of root.querySelectorAll(CARD_IMAGE_SELECTOR)) {
      const current = image.getAttribute("srcset") || "";
      const source =
        current.trim().split(/\s+/)[0].replace(/,$/, "") ||
        image.getAttribute("src");
      if (!source) continue;

      const srcset = cardImageSrcset(source);
      if (!srcset || srcset === current) continue;

      image.setAttribute("sizes", CARD_IMAGE_SIZES);
      image.setAttribute("srcset", srcset);
    }
  };

  /* ----------------------------------------------------------------- boot */

  const boot = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    let frame = 0;
    const sync = () => {
      frame = 0;
      setupMarquee(root);

      const footer = root.querySelector(".v2-zone-footer");
      if (footer) updateTierBadge(root, footer);
    };

    // The app renders after its own fetches; every re-render passes here.
    // sync() is idempotent, so its own mutations settle in one extra pass.
    // Card images are re-pointed straight away rather than on the next frame:
    // the browser starts on the app's own candidate as soon as React sets it,
    // and the sooner the srcset changes, the less of that is fetched. The
    // srcset filter catches the app giving a card a new image.
    new MutationObserver(() => {
      cropCardImages(root);
      if (!frame) frame = requestAnimationFrame(sync);
    }).observe(root, {
      attributes: true,
      attributeFilter: ["srcset"],
      characterData: true,
      childList: true,
      subtree: true,
    });

    cropCardImages(root);
    sync();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
